/**
 * M99 U7 - Widerstandsrechner
 * Farbcode ⇔ Widerstandswert Konverter mit SVG-Visualisierung
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PaletteIcon from '@mui/icons-material/Palette';
import TuneIcon from '@mui/icons-material/Tune';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';

const colorValues: Record<string, number> = {
  Schwarz: 0, Braun: 1, Rot: 2, Orange: 3, Gelb: 4,
  Grün: 5, Blau: 6, Violett: 7, Grau: 8, Weiß: 9
};

const colorHexMap: Record<string, string> = {
  Schwarz: '#000000', Braun: '#8B4513', Rot: '#FF0000', Orange: '#FFA500',
  Gelb: '#FFD700', Grün: '#008000', Blau: '#0000CD', Violett: '#800080',
  Grau: '#808080', Weiß: '#FFFFFF', Gold: '#FFD700', Silber: '#C0C0C0', Keine: '#DADFE6'
};

const multiplierValues: Record<string, number> = {
  Silber: 0.01, Gold: 0.1, Schwarz: 1, Braun: 10, Rot: 100,
  Orange: 1_000, Gelb: 10_000, Grün: 100_000, Blau: 1_000_000, Violett: 10_000_000
};

const toleranceValues: Record<string, number> = {
  Braun: 1, Rot: 2, Orange: 0.05, Gelb: 0.02, Grün: 0.5, Blau: 0.25,
  Violett: 0.1, Grau: 0.05, Gold: 5, Silber: 10, Keine: 20
};

const ppmValues: Record<string, number> = {
  Braun: 100, Rot: 50, Orange: 15, Gelb: 25, Blau: 10, Violett: 5
};

const e24Series = [
  1.0, 1.1, 1.2, 1.3, 1.5, 1.6, 1.8, 2.0, 2.2, 2.4, 2.7, 3.0,
  3.3, 3.6, 3.9, 4.3, 4.7, 5.1, 5.6, 6.2, 6.8, 7.5, 8.2, 9.1
];

const multiplierEntries = Object.entries(multiplierValues).map(([color, value]) => ({ color, value }));
const digitToColor = Object.entries(colorValues).reduce<Record<number, string>>((acc, [name, digit]) => {
  acc[digit] = name;
  return acc;
}, {});

const ringCountOptions = [
  { label: '3 Ringe', value: 3 },
  { label: '4 Ringe', value: 4 },
  { label: '5 Ringe', value: 5 },
  { label: '6 Ringe', value: 6 }
];

const toleranceSelectionOrder = ['Keine', 'Gold', 'Silber', 'Braun', 'Rot', 'Grün', 'Blau', 'Violett'];
const ppmSelection = ['Keine', ...Object.keys(ppmValues)];

const formatResistanceValue = (value: number): string => {
  const abs = Math.abs(value);
  const format = (val: number, fractionDigits = 3) =>
    val.toLocaleString('de-DE', {
      minimumFractionDigits: val % 1 === 0 ? 0 : Math.min(3, fractionDigits),
      maximumFractionDigits: fractionDigits
    });

  if (!Number.isFinite(value)) return '0';
  if (abs >= 1_000_000_000) return `${format(value / 1_000_000_000)} GΩ`;
  if (abs >= 1_000_000) return `${format(value / 1_000_000)} MΩ`;
  if (abs >= 1_000) return `${format(value / 1_000)} kΩ`;
  if (abs >= 1) return `${format(value)} Ω`;
  if (abs === 0) return '0 Ω';
  if (abs >= 1e-3) return `${format(value, 4)} Ω`;
  return `${value.toExponential(2).replace('.', ',')} Ω`;
};

const parseResistanceInput = (input: string): number | null => {
  if (!input.trim()) return null;
  let normalized = input.trim()
    .replace(/\s+/g, '')
    .replace(/Ω|ohm|Ω/gi, '')
    .replace(/meg/gi, 'M')
    .replace(/GΩ/gi, 'G')
    .replace(/kΩ/gi, 'k')
    .replace(/mΩ/gi, 'm')
    .replace(',', '.');

  const match = normalized.match(/^([0-9]*\.?[0-9]*)([kKmMgGrR]?)([0-9]*)$/);
  if (!match) {
    const value = Number.parseFloat(normalized);
    return Number.isFinite(value) ? value : null;
  }

  const [, leadingRaw, letterRaw, trailingRaw] = match;
  const leading = leadingRaw ? Number.parseFloat(leadingRaw) : 0;
  const trail = trailingRaw ? Number.parseFloat(`0.${trailingRaw}`) : 0;
  const total = leading + trail;

  switch (letterRaw) {
    case 'k': case 'K': return total * 1_000;
    case 'M': return total * 1_000_000;
    case 'G': return total * 1_000_000_000;
    case 'm': return total * 0.001;
    case 'R': case 'r': return total;
    default: {
      const value = Number.parseFloat(normalized);
      return Number.isFinite(value) ? value : null;
    }
  }
};

const renderMenuItemContent = (colorName: string) =>
  React.createElement(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: 1 } },
    React.createElement(Box, {
      sx: {
        width: 18, height: 12, borderRadius: 1,
        border: '1px solid rgba(0,0,0,0.4)',
        backgroundColor: colorHexMap[colorName] ?? '#FFFFFF'
      }
    }),
    colorName
  );

interface ResistorSVGProps {
  rings: string[];
  darkMode: boolean;
}

const ResistorSVG: React.FC<ResistorSVGProps> = ({ rings, darkMode }) => {
  const bodyColor = darkMode ? '#D4C5A0' : '#C4B59A';
  const wireColor = darkMode ? '#888' : '#666';
  const labelColor = darkMode ? tokens.colors.dark.text : tokens.colors.light.text;

  const ringPositions: Record<number, number[]> = {
    3: [35, 55, 75],
    4: [30, 50, 70, 90],
    5: [25, 45, 65, 85, 105],
    6: [22, 40, 58, 76, 94, 112]
  };

  const positions = ringPositions[rings.length] || ringPositions[4];

  return React.createElement('svg', { viewBox: '0 0 160 80', style: { maxWidth: '400px', width: '100%' } },
    React.createElement('line', { x1: '5', y1: '40', x2: '20', y2: '40', stroke: wireColor, strokeWidth: '2' }),
    React.createElement('line', { x1: '140', y1: '40', x2: '155', y2: '40', stroke: wireColor, strokeWidth: '2' }),
    React.createElement('rect', { x: '20', y: '28', width: '120', height: '24', fill: bodyColor, stroke: wireColor, strokeWidth: '1.5', rx: '2' }),
    ...rings.map((color, idx) =>
      React.createElement('g', { key: idx },
        React.createElement('rect', {
          x: positions[idx],
          y: '28',
          width: '6',
          height: '24',
          fill: colorHexMap[color] || '#CCC',
          stroke: 'rgba(0,0,0,0.3)',
          strokeWidth: '0.5'
        }),
        React.createElement('text', {
          x: positions[idx] + 3,
          y: '70',
          fontSize: '7',
          fill: labelColor,
          textAnchor: 'middle'
        }, color)
      )
    )
  );
};

const infoTopics = [
  {
    title: 'Farbcode 4-Ring System',
    html: `
      <b>Ring-Bedeutung:</b><br>
      1. Ring: 1. Ziffer<br>
      2. Ring: 2. Ziffer<br>
      3. Ring: Multiplikator (10er-Potenz)<br>
      4. Ring: Toleranz<br><br>
      <b>Beispiel:</b> Braun-Schwarz-Rot-Gold<br>
      → 1·0 × 100 = <b>1000 Ω (1 kΩ) ±5%</b>
    `
  },
  {
    title: 'Farbcode 5-Ring System',
    html: `
      <b>Ring-Bedeutung:</b><br>
      1. Ring: 1. Ziffer<br>
      2. Ring: 2. Ziffer<br>
      3. Ring: 3. Ziffer<br>
      4. Ring: Multiplikator<br>
      5. Ring: Toleranz<br><br>
      <b>Präzisionswiderstand</b> (3 Ziffern = genauere Werte)<br>
      Beispiel: Braun-Schwarz-Schwarz-Braun-Braun<br>
      → 1·0·0 × 10 = <b>1000 Ω ±1%</b>
    `
  },
  {
    title: 'Farbtabelle',
    html: `
      <table style="width:100%; border-collapse: collapse;">
        <tr><th style="text-align:left; padding:4px;">Farbe</th><th>Ziffer</th><th>Multiplikator</th><th>Toleranz</th></tr>
        <tr><td style="padding:4px;">Schwarz</td><td>0</td><td>×1</td><td>-</td></tr>
        <tr><td style="padding:4px;">Braun</td><td>1</td><td>×10</td><td>±1%</td></tr>
        <tr><td style="padding:4px;">Rot</td><td>2</td><td>×100</td><td>±2%</td></tr>
        <tr><td style="padding:4px;">Orange</td><td>3</td><td>×1k</td><td>-</td></tr>
        <tr><td style="padding:4px;">Gelb</td><td>4</td><td>×10k</td><td>-</td></tr>
        <tr><td style="padding:4px;">Grün</td><td>5</td><td>×100k</td><td>±0.5%</td></tr>
        <tr><td style="padding:4px;">Blau</td><td>6</td><td>×1M</td><td>±0.25%</td></tr>
        <tr><td style="padding:4px;">Violett</td><td>7</td><td>×10M</td><td>±0.1%</td></tr>
        <tr><td style="padding:4px;">Grau</td><td>8</td><td>-</td><td>±0.05%</td></tr>
        <tr><td style="padding:4px;">Weiß</td><td>9</td><td>-</td><td>-</td></tr>
        <tr><td style="padding:4px;"><b>Gold</b></td><td>-</td><td>×0.1</td><td>±5%</td></tr>
        <tr><td style="padding:4px;"><b>Silber</b></td><td>-</td><td>×0.01</td><td>±10%</td></tr>
      </table>
    `
  },
  {
    title: 'Merksätze',
    html: `
      <b>Klassische Eselsbrücke:</b><br>
      <i>"Schwarz Braun Rot Orange Gelb<br>
      Grün Blau Violett Grau Weiß"</i><br><br>
      
      <b>Kreativ:</b><br>
      <i>"0 Schwarze 1 Brave 2 Rote 3 Orangen<br>
      4 Gelbe 5 Grüne 6 Blaue 7 Violette<br>
      8 Graue 9 Weiße"</i><br><br>
      
      <b>Temperaturkoeffizient (6. Ring):</b><br>
      Gibt Änderung in ppm/K an (Präzisionsbauteile)
    `
  }
];

type SnackbarState = { open: boolean; message: string; severity: 'success' | 'info' | 'warning' | 'error' };

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewWiderstand } = useAuth();
  const [calculationMode, setCalculationMode] = useState<'colorToValue' | 'valueToColor'>('colorToValue');
  const [ringCount, setRingCount] = useState<number>(4);
  const [selectedColors, setSelectedColors] = useState({
    ring1: 'Braun', ring2: 'Schwarz', ring3: 'Braun',
    multiplier: 'Rot', tolerance: 'Gold', ppm: 'Braun'
  });
  const [colorModeResult, setColorModeResult] = useState('');
  const [targetResistance, setTargetResistance] = useState('');
  const [targetTolerance, setTargetTolerance] = useState('Gold');
  const [valueModeResult, setValueModeResult] = useState('');
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoTopicIndex, setInfoTopicIndex] = useState(0);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });

  const valueColorOptions = useMemo(() => Object.keys(colorValues), []);
  const multiplierOptions = useMemo(() => Object.keys(multiplierValues), []);

  useEffect(() => {
    setSelectedColors(prev => {
      if (ringCount === 3 && prev.tolerance !== 'Keine') return { ...prev, tolerance: 'Keine' };
      if (ringCount !== 3 && prev.tolerance === 'Keine') return { ...prev, tolerance: 'Gold' };
      return prev;
    });
  }, [ringCount]);

  useEffect(() => {
    if (calculationMode === 'colorToValue') calculateResistorValueFromColors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationMode, ringCount, selectedColors]);

  const handleReset = useCallback(() => {
    setSelectedColors({
      ring1: 'Braun', ring2: 'Schwarz', ring3: 'Braun',
      multiplier: 'Rot', tolerance: 'Gold', ppm: 'Braun'
    });
    setTargetResistance('');
    setTargetTolerance('Gold');
    setColorModeResult('');
    setValueModeResult('');
    setSnackbar({ open: true, message: 'Alle Felder zurückgesetzt', severity: 'info' });
  }, []);

  const handleCopyResult = useCallback(() => {
    const resultToCopy = calculationMode === 'colorToValue' ? colorModeResult : valueModeResult;
    if (!resultToCopy) {
      setSnackbar({ open: true, message: 'Kein Ergebnis zum Kopieren', severity: 'warning' });
      return;
    }
    const textVersion = resultToCopy.replace(/<br>/g, '\n').replace(/<\/?[^>]+(>|$)/g, '');
    navigator.clipboard.writeText(textVersion).then(() => {
      setSnackbar({ open: true, message: 'Ergebnis kopiert', severity: 'success' });
    });
  }, [calculationMode, colorModeResult, valueModeResult]);

  const updateColorSelection = (field: keyof typeof selectedColors) => (event: any) => {
    setSelectedColors(prev => ({ ...prev, [field]: event.target.value }));
  };

  const calculateResistorValueFromColors = () => {
    const val1 = colorValues[selectedColors.ring1];
    const val2 = colorValues[selectedColors.ring2];
    const val3 = ringCount >= 5 ? colorValues[selectedColors.ring3] : 0;
    const multiplier = multiplierValues[selectedColors.multiplier] ?? 1;
    
    if (val1 === undefined || val2 === undefined || (ringCount >= 5 && val3 === undefined)) {
      setColorModeResult('Auswahl unvollständig.');
      return;
    }

    const baseValue = ringCount >= 5 ? (val1 * 100 + val2 * 10 + val3) : (val1 * 10 + val2);
    const finalValue = baseValue * multiplier;
    const toleranceColor = ringCount === 3 ? 'Keine' : selectedColors.tolerance;
    const tolerance = toleranceValues[toleranceColor] ?? 20;
    const ppmColor = ringCount === 6 ? selectedColors.ppm : 'Keine';
    const ppm = ppmColor !== 'Keine' ? ppmValues[ppmColor] : undefined;

    let html = `<b>Widerstandswert:</b> ${formatResistanceValue(finalValue)}`;
    if (tolerance) html += ` <b>±${tolerance}%</b>`;
    if (ppm !== undefined) html += `<br><b>Temp.-Koeff.:</b> ${ppm} ppm/K`;

    setColorModeResult(html);
  };

  const handleFindResistorColors = () => {
    const parsedValue = parseResistanceInput(targetResistance);
    if (!parsedValue || parsedValue <= 0) {
      setValueModeResult('Ungültiger Wert. Beispiele: 100, 4k7, 2.2M');
      return;
    }

    let bestMatchValue = -1;
    let bestDiff = Number.POSITIVE_INFINITY;
    let bestSeries = e24Series[0];
    let bestMultiplier = multiplierEntries[0];

    for (const series of e24Series) {
      for (const entry of multiplierEntries) {
        const currentValue = series * entry.value;
        const diff = Math.abs(parsedValue - currentValue);
        if (diff < bestDiff || (diff === bestDiff && currentValue < bestMatchValue)) {
          bestDiff = diff;
          bestMatchValue = currentValue;
          bestSeries = series;
          bestMultiplier = entry;
        }
      }
    }

    if (bestMatchValue <= 0) bestMatchValue = parsedValue;

    const digits = Math.round(bestSeries * 10);
    const d1 = Math.floor(digits / 10);
    const d2 = digits % 10;
    const color1 = digitToColor[d1];
    const color2 = digitToColor[d2];
    const multiplierColor = bestMultiplier.color;
    const toleranceColor = targetTolerance;

    if (!color1 || !color2 || !multiplierColor) {
      setValueModeResult('Farbcode konnte nicht bestimmt werden.');
      return;
    }

    const diff = Math.abs(bestMatchValue - parsedValue);
    const diffPercent = parsedValue !== 0 ? (diff / parsedValue) * 100 : 0;

    let html = `<b>Nächster E24-Wert:</b> ${formatResistanceValue(bestMatchValue)}<br>`;
    html += `<b>Farbcode (4-Ring):</b> ${color1} · ${color2} · ${multiplierColor} · ${toleranceColor}`;
    if (diff > 0.0001) {
      html += `<br><i>Abweichung: ${formatResistanceValue(diff)} (${diffPercent.toFixed(2)}%)</i>`;
    }

    setValueModeResult(html);
  };

  const actionButtons = useMemo(() => [
    React.createElement(Tooltip, { key: 'info', title: 'Farbcode-Tabellen & Infos', children: React.createElement(IconButton, { onClick: () => setInfoDialogOpen(true), size: 'small' }, React.createElement(InfoOutlinedIcon)) }),
    React.createElement(Tooltip, { key: 'reset', title: 'Alle Felder zurücksetzen', children: React.createElement(IconButton, { onClick: handleReset, size: 'small' }, React.createElement(RestartAltIcon)) }),
    React.createElement(Tooltip, { key: 'copy', title: 'Ergebnis kopieren', children: React.createElement('span', {}, React.createElement(IconButton, { onClick: handleCopyResult, size: 'small', disabled: !colorModeResult && !valueModeResult }, React.createElement(ContentCopyIcon))) })
  ], [colorModeResult, handleCopyResult, handleReset, valueModeResult]);

  const displayRings = useMemo(() => {
    const rings = [selectedColors.ring1, selectedColors.ring2];
    if (ringCount >= 5) rings.push(selectedColors.ring3);
    rings.push(selectedColors.multiplier);
    if (ringCount >= 4) rings.push(selectedColors.tolerance);
    if (ringCount === 6) rings.push(selectedColors.ppm);
    return rings;
  }, [ringCount, selectedColors]);

  if (!canViewWiderstand) {
    return React.createElement(UnitPageShell, { title: 'M99 \u00b7 Widerstandsrechner', showTTS: false },
      React.createElement(Alert, { severity: 'error' }, 'Keine Berechtigung fuer den Widerstandsrechner.')
    );
  }

  return React.createElement(React.Fragment, {},
    React.createElement(UnitPageShell, {
      title: 'M99 · Widerstandsrechner',
      actions: actionButtons,
      showTTS: false
    },
      React.createElement(Stack, { spacing: 4 },
        React.createElement(Card, { elevation: 2 },
          React.createElement(CardContent, {},
            React.createElement(RadioGroup, {
              row: true,
              value: calculationMode,
              onChange: (e: any) => setCalculationMode(e.target.value as 'colorToValue' | 'valueToColor')
            },
              React.createElement(FormControlLabel, { value: 'colorToValue', control: React.createElement(Radio, { color: 'primary' }), label: 'Farbe → Wert' }),
              React.createElement(FormControlLabel, { value: 'valueToColor', control: React.createElement(Radio, { color: 'primary' }), label: 'Wert → Farbe' })
            )
          )
        ),

        calculationMode === 'colorToValue' ?
          React.createElement(Card, { elevation: 2 },
            React.createElement(CardContent, {},
              React.createElement(Typography, { variant: 'h6', gutterBottom: true, sx: { fontWeight: 600 } }, 'Farbcode auswählen'),

              React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 } },
                React.createElement(TextField, {
                  select: true,
                  fullWidth: true,
                  label: 'Anzahl Ringe',
                  value: ringCount,
                  onChange: (e: any) => setRingCount(Number(e.target.value))
                },
                  ringCountOptions.map(opt =>
                    React.createElement(MenuItem, { key: opt.value, value: opt.value }, opt.label)
                  )
                )
              ),

              React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 } },
                React.createElement(TextField, { select: true, fullWidth: true, label: 'Ring 1', value: selectedColors.ring1, onChange: updateColorSelection('ring1') },
                  valueColorOptions.map(c => React.createElement(MenuItem, { key: c, value: c }, renderMenuItemContent(c)))
                ),
                React.createElement(TextField, { select: true, fullWidth: true, label: 'Ring 2', value: selectedColors.ring2, onChange: updateColorSelection('ring2') },
                  valueColorOptions.map(c => React.createElement(MenuItem, { key: c, value: c }, renderMenuItemContent(c)))
                ),
                ringCount >= 5 && React.createElement(TextField, { select: true, fullWidth: true, label: 'Ring 3', value: selectedColors.ring3, onChange: updateColorSelection('ring3') },
                  valueColorOptions.map(c => React.createElement(MenuItem, { key: c, value: c }, renderMenuItemContent(c)))
                ),
                React.createElement(TextField, { select: true, fullWidth: true, label: 'Multiplikator', value: selectedColors.multiplier, onChange: updateColorSelection('multiplier') },
                  multiplierOptions.map(c => React.createElement(MenuItem, { key: c, value: c }, renderMenuItemContent(c)))
                ),
                React.createElement(TextField, {
                  select: true,
                  fullWidth: true,
                  label: 'Toleranz',
                  value: ringCount === 3 ? 'Keine' : selectedColors.tolerance,
                  onChange: updateColorSelection('tolerance'),
                  disabled: ringCount === 3
                },
                  toleranceSelectionOrder.map(c => React.createElement(MenuItem, { key: c, value: c }, renderMenuItemContent(c)))
                ),
                ringCount === 6 && React.createElement(TextField, { select: true, fullWidth: true, label: 'Temp.-Koeff.', value: selectedColors.ppm, onChange: updateColorSelection('ppm') },
                  ppmSelection.map(c => React.createElement(MenuItem, { key: c, value: c }, renderMenuItemContent(c)))
                )
              ),

              React.createElement(Button, {
                variant: 'contained',
                size: 'large',
                startIcon: React.createElement(PaletteIcon),
                onClick: calculateResistorValueFromColors,
                sx: { backgroundColor: tokens.colors.primary, '&:hover': { backgroundColor: tokens.colors.primaryHover } }
              }, 'Berechnen'),

              colorModeResult && React.createElement(React.Fragment, {},
                React.createElement(Divider, { sx: { my: 3 } }),
                React.createElement(Stack, { spacing: 2 },
                  React.createElement(Typography, {
                    variant: 'body1',
                    sx: { lineHeight: 1.8, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text },
                    dangerouslySetInnerHTML: { __html: colorModeResult }
                  }),
                  React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', mt: 2 } },
                    React.createElement(ResistorSVG, { rings: displayRings, darkMode })
                  )
                )
              )
            )
          ) :
          React.createElement(Card, { elevation: 2 },
            React.createElement(CardContent, {},
              React.createElement(Typography, { variant: 'h6', gutterBottom: true, sx: { fontWeight: 600 } }, 'Zielwiderstand eingeben'),

              React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 } },
                React.createElement(TextField, {
                  fullWidth: true,
                  label: 'Widerstandswert',
                  value: targetResistance,
                  onChange: (e: any) => setTargetResistance(e.target.value),
                  placeholder: 'z.B. 4k7, 2.2M, 100',
                  helperText: 'Formate: 4k7, 2M2, 100R, 0.1'
                }),
                React.createElement(TextField, { select: true, fullWidth: true, label: 'Gewünschte Toleranz', value: targetTolerance, onChange: (e: any) => setTargetTolerance(e.target.value) },
                  toleranceSelectionOrder.map(c => React.createElement(MenuItem, { key: c, value: c }, renderMenuItemContent(c)))
                )
              ),

              React.createElement(Button, {
                variant: 'contained',
                size: 'large',
                startIcon: React.createElement(TuneIcon),
                onClick: handleFindResistorColors,
                sx: { backgroundColor: tokens.colors.primary, '&:hover': { backgroundColor: tokens.colors.primaryHover } }
              }, 'Farbcode bestimmen'),

              valueModeResult && React.createElement(React.Fragment, {},
                React.createElement(Divider, { sx: { my: 3 } }),
                React.createElement(Typography, {
                  variant: 'body1',
                  sx: { lineHeight: 1.8, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text },
                  dangerouslySetInnerHTML: { __html: valueModeResult }
                })
              )
            )
          )
      )
    ),

    React.createElement(Dialog, { open: infoDialogOpen, onClose: () => setInfoDialogOpen(false), maxWidth: 'md', fullWidth: true },
      React.createElement(DialogTitle, { sx: { backgroundColor: tokens.colors.primary, color: '#fff' } }, 'Informationen · Widerstandsfarbcode'),
      React.createElement(DialogContent, { dividers: true },
        React.createElement(Stack, { spacing: 2, sx: { mt: 1 } },
          infoTopics.map((topic, idx) =>
            React.createElement(Box, { key: idx },
              React.createElement(Button, {
                fullWidth: true,
                variant: infoTopicIndex === idx ? 'contained' : 'outlined',
                onClick: () => setInfoTopicIndex(idx),
                sx: { justifyContent: 'flex-start', mb: 1 }
              }, topic.title),
              infoTopicIndex === idx && React.createElement(Box, { sx: { p: 2, backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', borderRadius: 1 } },
                React.createElement(Typography, {
                  variant: 'body2',
                  sx: { lineHeight: 1.8 },
                  dangerouslySetInnerHTML: { __html: topic.html }
                })
              )
            )
          )
        )
      ),
      React.createElement(DialogActions, {},
        React.createElement(Button, { onClick: () => setInfoDialogOpen(false), color: 'primary' }, 'Schließen')
      )
    ),

    React.createElement(Snackbar, {
      open: snackbar.open,
      autoHideDuration: 3500,
      onClose: () => setSnackbar(prev => ({ ...prev, open: false })),
      anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
    },
      React.createElement(Alert, { severity: snackbar.severity, onClose: () => setSnackbar(prev => ({ ...prev, open: false })) },
        snackbar.message
      )
    )
  );
};

export default Page;
